import React, { useMemo, useState } from "react"

import WarningApproveModal from "@/components/common/Modals/WarningApproveModal"
import { type Location } from "@/graphql/codegen/graphql"
import LocationAddedModal from "@/screens/Companies/create/components/LocationAddedModal"
import { AddAddressDialog } from "@/screens/Companies/edit/components/locations/dialogs/AddAddressDialog"
import { AddNotesDialog } from "@/screens/Companies/edit/components/locations/dialogs/AddNotesDialog"
import { EditAccountManagerDialog } from "@/screens/Companies/edit/components/locations/dialogs/EditAccountManagerDialog"
import { EditAddressDialog } from "@/screens/Companies/edit/components/locations/dialogs/EditAddressDialog"
import { EditLocationContactMethodsDialog } from "@/screens/Companies/edit/components/locations/dialogs/EditLocationContactMethodsDialog"
import { EditNotesDialog } from "@/screens/Companies/edit/components/locations/dialogs/EditNotesDialog"
import { EditSalesPersonDialog } from "@/screens/Companies/edit/components/locations/dialogs/EditSalesPersonDialog"
import {
  LocationConfirmationDialogs,
  type LocationDialogType,
  LocationMenuDialogs,
} from "@/screens/Companies/edit/components/locations/types/dialogs"
import type {
  AddAddressQueryProps,
  AddNotesQueryProps,
  AddressFields,
  ContactFields,
  EditAddressQueryProps,
  EditContactQueryProps,
  EditNotesQueryProps,
  NotesSchema,
  UpdateAccountManagerProps,
  UpdateSalesmanProps,
} from "@/screens/Companies/types/dialogProps"
import type { DialogCloseAction } from "@/types/common"

interface LocationTableDialogsProps {
  openedDialog: LocationDialogType
  activeEditLocation: Location | undefined
  setOpenedDialog: (dialog: LocationDialogType) => void
  handleCreateSubmit: (data: AddAddressQueryProps) => void
  handleUpdateSubmit: (
    data:
      | EditContactQueryProps
      | EditAddressQueryProps
      | EditNotesQueryProps
      | AddNotesQueryProps
      | UpdateAccountManagerProps
      | UpdateSalesmanProps
  ) => void
  prevOpenedDialog: LocationDialogType
  isCustomerCompanyType: boolean
}

const cancelMessages = {
  [LocationMenuDialogs.ADD_ADDRESS]: {
    title: "Are you sure you want to cancel adding a location?",
    description:
      "By cancelling now this location will not be added to the company. Click on “Go Back” if this action was a mistake.",
  },
  [LocationMenuDialogs.ADD_NOTES]: {
    title: "Are you sure you want to cancel adding a note?",
    description:
      "By cancelling now this note will not be added to the company. Click on “Go Back” if this action was a mistake.",
  },
  [LocationMenuDialogs.EDIT_ADDRESS]: {
    title: "Are you sure you want to cancel editing a location?",
    description:
      "By cancelling now this location will not be updated. Click on “Go Back” if this action was a mistake.",
  },
  [LocationMenuDialogs.EDIT_CONTACT]: {
    title: "Are you sure you want to cancel editing a contact?",
    description: "By cancelling now this contact will not be updated. Click on “Go Back” if this action was a mistake.",
  },
  [LocationMenuDialogs.EDIT_NOTES]: {
    title: "Are you sure you want to cancel editing a note?",
    description: "By cancelling now this note will not be updated. Click on “Go Back” if this action was a mistake.",
  },
  [LocationMenuDialogs.EDIT_ACCOUNT_MANAGER]: {
    title: "Are you sure you want to cancel editing a account manager?",
    description:
      "By cancelling now this location will not be updated. Click on “Go Back” if this action was a mistake.",
  },
  [LocationMenuDialogs.EDIT_SALES_PERSON]: {
    title: "Are you sure you want to cancel editing a salesperson?",
    description:
      "By cancelling now this location will not be updated. Click on “Go Back” if this action was a mistake.",
  },
}

export const LocationTableDialogs: React.FC<LocationTableDialogsProps> = ({
  openedDialog,
  activeEditLocation,
  handleCreateSubmit,
  handleUpdateSubmit,
  setOpenedDialog,
  prevOpenedDialog,
  isCustomerCompanyType,
}) => {
  const [dialogData, setDialogData] = useState<AddressFields | ContactFields | NotesSchema | Record<string, any>>({})

  const handleSuccessDialog = (action: "addMore" | DialogCloseAction) => {
    if (action === "addMore") {
      setOpenedDialog(LocationMenuDialogs.ADD_ADDRESS)
      return
    }

    setOpenedDialog(null)
  }
  const handleDialogClose = ({
    data,
    reason,
  }: {
    data: AddressFields | ContactFields | NotesSchema | Record<string, any>
    reason: DialogCloseAction
  }) => {
    if (reason === "success" || reason === "close") {
      setOpenedDialog(null)
      return
    }

    if (reason === "cancel") {
      setDialogData(data)
      setOpenedDialog(LocationConfirmationDialogs.CANCEL)
    }
  }

  const handleCancelDialogClose = (reason: DialogCloseAction) => {
    if (reason === "cancel") {
      setOpenedDialog(prevOpenedDialog)
      return
    }

    setOpenedDialog(null)
    setDialogData({})
  }

  const messages = useMemo(
    () => cancelMessages[prevOpenedDialog as LocationMenuDialogs] ?? { title: "", description: "" },
    [prevOpenedDialog]
  )

  return (
    <>
      <LocationAddedModal onClose={handleSuccessDialog} open={openedDialog === LocationConfirmationDialogs.SUCCESS} />
      {openedDialog === LocationConfirmationDialogs.CANCEL && (
        <WarningApproveModal messages={messages} onClose={handleCancelDialogClose} />
      )}
      {openedDialog === LocationMenuDialogs.ADD_ADDRESS && (
        <AddAddressDialog
          data={dialogData as AddressFields}
          onClose={handleDialogClose}
          onSubmit={handleCreateSubmit}
          isCustomerCompanyType={isCustomerCompanyType}
        />
      )}
      {openedDialog === LocationMenuDialogs.EDIT_ADDRESS && (
        <EditAddressDialog
          data={dialogData as AddressFields}
          activeEditLocation={activeEditLocation}
          onClose={handleDialogClose}
          onSubmit={handleUpdateSubmit}
        />
      )}

      {openedDialog === LocationMenuDialogs.EDIT_CONTACT && (
        <EditLocationContactMethodsDialog
          data={dialogData as ContactFields}
          activeEditLocation={activeEditLocation}
          onClose={handleDialogClose}
          onSubmit={handleUpdateSubmit}
        />
      )}
      {openedDialog === LocationMenuDialogs.ADD_NOTES && (
        <AddNotesDialog onClose={handleDialogClose} onSubmit={handleUpdateSubmit} data={dialogData as NotesSchema} />
      )}
      {openedDialog === LocationMenuDialogs.EDIT_NOTES && (
        <EditNotesDialog
          data={dialogData as NotesSchema}
          activeEditLocation={activeEditLocation}
          onClose={handleDialogClose}
          onSubmit={handleUpdateSubmit}
        />
      )}
      {openedDialog === LocationMenuDialogs.EDIT_ACCOUNT_MANAGER && (
        <EditAccountManagerDialog
          data={activeEditLocation as Location}
          onClose={handleDialogClose}
          onSubmit={handleUpdateSubmit}
        />
      )}

      {openedDialog === LocationMenuDialogs.EDIT_SALES_PERSON && (
        <EditSalesPersonDialog
          data={dialogData as UpdateSalesmanProps}
          activeEditLocation={activeEditLocation}
          onClose={handleDialogClose}
          onSubmit={handleUpdateSubmit}
        />
      )}
    </>
  )
}
